// ─── Role & Command types ─

export type Role =
  | "REMOTE_SUPERVISOR"
  | "OPERATOR_SUPERVISOR"
  | "GENERIC_OPERATOR"
  | "CUSTOMER_VIEWER"
  | "ORCHESTRATOR_DEVELOPER";

export type Command =
  | "LOCATION_SPOOFING"
  | "ORCHESTRATOR_ENG_TOOLS"
  | "MAPPING"
  | "MISSION_CREATION"
  | "MISSION_DELETION"
  | "START_AUTONOMY"
  | "START_AUTONOMY_MODIFIED_MISSION"
  | "VIEW_DUNE_STATUS"
  | "PAUSE_MISSION"
  | "RESUME_MISSION"
  | "ALL_STOP"
  | "QUIT_MISSION"
  | "AUTONOMY_OFF"
  | "COME_TO_LOAD_POINT"
  | "READY_TO_LOAD"
  | "FINISHED_LOADING";

// ─── Permission matrix ───────────────────────────────────────────────────────
// Each entry: { REMOTE_SUPERVISOR, OPERATOR_SUPERVISOR, GENERIC_OPERATOR, CUSTOMER_VIEWER, ORCHESTRATOR_DEVELOPER }
// true = enabled for that role, false = disabled
// ✓? entries (MISSION_CREATION for REMOTE_SUPERVISOR / OPERATOR_SUPERVISOR) are set to true.

const PERMISSIONS: Record<Command, Record<Role, boolean>> = {
  LOCATION_SPOOFING: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: false,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  ORCHESTRATOR_ENG_TOOLS: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: false,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  MAPPING: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: false,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  MISSION_CREATION: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  MISSION_DELETION: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  START_AUTONOMY: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  START_AUTONOMY_MODIFIED_MISSION: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: false,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  VIEW_DUNE_STATUS: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: true,
    ORCHESTRATOR_DEVELOPER: true,
  },
  PAUSE_MISSION: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  RESUME_MISSION: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  ALL_STOP: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  QUIT_MISSION: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: false,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  AUTONOMY_OFF: {
    REMOTE_SUPERVISOR: true,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  COME_TO_LOAD_POINT: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  READY_TO_LOAD: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
  FINISHED_LOADING: {
    REMOTE_SUPERVISOR: false,
    OPERATOR_SUPERVISOR: true,
    GENERIC_OPERATOR: true,
    CUSTOMER_VIEWER: false,
    ORCHESTRATOR_DEVELOPER: true,
  },
};

// ─── Org-level deny list ─────────────────────────────────────────────────────
// Commands that are unconditionally blocked for specific organizations,
// regardless of the user's role.
const ORG_DENIED_COMMANDS: Record<string, ReadonlySet<Command>> = {
  "DUNE-QUAL": new Set(["LOCATION_SPOOFING"]),
};

// ─── Checker function ────────────────────────────────────────────────────────

/**
 * Check whether a given role is allowed to execute a given command.
 *
 * @param role    - The user's role (SCREAMING_SNAKE_CASE)
 * @param command - The command / action to check
 * @param orgName - (optional) The current organization name. When provided,
 *                  org-level deny rules are evaluated first.
 * @returns true if the role is permitted, false otherwise (including unknown role/command)
 */
export function hasPermission(role: string, command: string, orgName?: string): boolean {
  // Org-level deny takes precedence over role-based permissions
  if (orgName && ORG_DENIED_COMMANDS[orgName]?.has(command as Command)) {
    return false;
  }

  const normalizedRole = role === "OPERATOR_AND_SUPERVISOR" ? "OPERATOR_SUPERVISOR" : role;
  // console.log(`Checking permission for role: ${normalizedRole}, command: ${command}`);
  const commandEntry = PERMISSIONS[command as Command];
  if (!commandEntry) return false;

  const allowed = commandEntry[normalizedRole as Role];
  // console.log(`Permission result for role: ${normalizedRole}, command: ${command} => ${allowed}`);
  return typeof allowed === "boolean" ? allowed : false;
}
